import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BrightsideUiModule, HeaderLabelCellConfig } from '@brightside/brightside-ui';
import { NewSavingsAccountService } from '@brightside-web/desktop/feature/savings-account';
import { CurrencyPipe, NgIf } from '@angular/common';
import { filter, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SavingsAccount } from '@brightside-web/desktop/feature/savings-account';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';

@Component({
    selector: 'brightside-web-savings-complete-card',
    templateUrl: './savings-complete-card.component.html',
    styleUrls: ['./savings-complete-card.component.scss'],
    imports: [BrightsideUiModule, NgIf, TranslateModule],
    providers: [CurrencyPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms ease-out', style({ opacity: 1 }))
            ])
        ])
    ]
})
export class SavingsCompleteCardComponent implements OnDestroy {
  private _savingsSvc: NewSavingsAccountService = inject(NewSavingsAccountService);
  private _router: Router = inject(Router);
  private _currencyPipe: CurrencyPipe = inject(CurrencyPipe);
  private _cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _translateSvc: TranslateService = inject(TranslateService);

  savingsAccount$ = this._savingsSvc.currentAccountInfo;
  account: SavingsAccount;
  accountCellConfig: HeaderLabelCellConfig;
  private savingsAccount$Sub: Subscription;
  savingsState: string;
  private _serviceInventory = inject(ServiceInventoryService);
  private _sub = new Subscription();
  serviceState = this._savingsSvc.serviceState;

  constructor() {
    this._sub.add(this._serviceInventory.getService('savings')
      .pipe(
        filter(service => !!service),
        tap(service => {
          switch (service.state) {
            case ServiceState.COMPLETE:
              this.savingsState = ServiceState.COMPLETE;
              setTimeout(()=>this.setCompleteDate());
              break;
            case ServiceState.PENDING:
              this.savingsState = ServiceState.PENDING;
              setTimeout(()=>this.setPendingData());
              break;
            case ServiceState.ERROR:
              this.savingsState = ServiceState.ERROR;
              setTimeout(()=>this.setErrorData());
              break;
          }
        })
      ).subscribe()
    );

    this._sub.add(
      this.serviceState.pipe(
        tap(state => {
          switch (state) {
            case 'error':
              this.savingsState = ServiceState.ERROR;
              setTimeout(()=>this.setErrorData());
              break;
            case 'no-error':
              this.savingsState = ServiceState.COMPLETE;
              setTimeout(()=>this.setCompleteDate());
              break;
          }
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  routeToSavings() {
    if (this.savingsState === ServiceState.COMPLETE) {
      this._router.navigate(['accounts', 'savings', 'dashboard']);
    } else if (this.savingsState === ServiceState.ERROR) {
      this.accountCellConfig = {
        leftTitle: this._translateSvc.instant('SPENDING'),
        lottieLoading: true        
      }
      setTimeout(()=>{this.retrySpending();}, 3000);
      this._cdRef.detectChanges();
    } else {
      return;
    }
  }

  private setCompleteDate() {
    this.savingsAccount$.pipe(
      filter(account => !!account),
      tap( account=> {
        if (account) {
          const autoSaveStatus = account.fundsSource?.autoSaveSchedule.status === 'active' ? 'AUTOSAVE_ON' : 'AUTOSAVE_OFF';
          this.accountCellConfig = {
              leftTitle: account.accountDisplayName,
              rightIconName: 'chevron_right',
              rightSubtitle: this._translateSvc.instant(autoSaveStatus),
              rightTitle: this._currencyPipe.transform(account.accountBalance)              
          }
          this._cdRef.detectChanges();
        }
      })
    ).subscribe();
  }

  private setPendingData() {
    this.accountCellConfig = {
      leftTitle: this._translateSvc.instant('SAVINGS')
    };
    this._cdRef.detectChanges();
  }

  private setErrorData() {
    this.accountCellConfig = {
      leftTitle: this._translateSvc.instant('ACCOUNT_ERROR_TITLE'),
      leftSubtitle: this._translateSvc.instant('ACCOUNT_ERROR_DESC')      
    }
    this._cdRef.detectChanges();
  }

  private retrySpending() {
    setTimeout(() => {
      this._savingsSvc.getSavingsAccounts(true);
    }, 3000);
  }
}
